/* ShowKeysRenderer.js */

"use strict";

const uiCore = require("@gluenetworks/steve_ui_core");
const TextareaControl = uiCore.Control.Textfield.Textarea;
const ButtonControl = uiCore.Control.Action.Button;

const MODAL_TITLE = "SSH Keys";
const PRIVATE_KEY_LABEL = "private SSH key";
const PUBLIC_KEY_LABEL = "public SSH key";

class ShowKeysRenderer {
    constructor(model) {
        /**
         * @type {AddLicenseComponent}
         */
        this._model = model;

        this._rootElem = this._createRootElement();

        this._privateKeyArea = new TextareaControl({
            darkTheme: true
        });
        this._publicKeyArea = new TextareaControl({
            darkTheme: true
        });

        this._closeButton = new ButtonControl({ label: "Close", darkTheme: true });

        this._registerListeners();
        this.render();
    }

    get rootElement() {
        return this.getRootElement();
    }

    get model() {
        return this.getModel();
    }

    /*******************************************/
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    render() {
        if (this._renderProm === null) {
            return Promise.resolve();
        }
        this._renderProm = this._renderProm || this._render();
        return this._renderProm;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    getRootElement() {
        return this._rootElem;
    }

    getModel() {
        return this._model;
    }

    /********************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS    */
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.addListener("Data", (this._onData = this._onData.bind(this)));
        this._closeButton.addListener(
            "UserClick",
            (this.close = this._model.closePopUp.bind(this._model))
        );
    }

    _unregisterListeners() {
        this._closeButton.removeListener("UserClick", this.close);
        this._model.removeListener("Data", this._onData);
    }

    _registerCopyListener(button, handler) {
        button.addListener("UserClick", handler);
        this._model.once("Shutdown", () => {
            button.removeListener("UserClick", handler);
            button.shutdown();
        });
    }

    _render() {
        this._rootElem.innerHTML = "";
        const root = document.createDocumentFragment();
        const title = document.createElement("h2");

        title.innerText = MODAL_TITLE;
        root.appendChild(title);
        root.appendChild(this._createKeySection(PRIVATE_KEY_LABEL, this._privateKeyArea));
        root.appendChild(this._createKeySection(PUBLIC_KEY_LABEL, this._publicKeyArea));
        root.appendChild(this._createActionsContainer());

        return new Promise((resolve /*,reject*/) => {
            this._rootElem.appendChild(root);
            resolve();
        });
    }

    _shutdown() {
        this._unregisterListeners();
        this._rootElem.remove();
        this._rootElem = null;

        return Promise.all([
            this._closeButton.shutdown(),
            this._privateKeyArea.shutdown(),
            this._publicKeyArea.shutdown()
        ])
            .then(() => {
                this._model = null;
                this._shutdownProm = null;
                this._renderProm = null;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    /********************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS   */
    /* START PRIVATE RENDERER METHODS           */
    /********************************************/

    _createRootElement() {
        const rootElement = document.createElement("div");
        rootElement.classList.add("show-keys", "modal_feedback_container");
        rootElement.component = this._model;
        return rootElement;
    }

    _createKeySection(name, keyAreaComponent) {
        const section = document.createElement("section");
        const label = document.createElement("label");
        const copyButton = new ButtonControl({
            label: "Copy",
            darkTheme: true
        });

        this._registerCopyListener(copyButton, () => {
            keyAreaComponent.renderer._textField.select();
            document.execCommand("copy");
        });

        section.className = "key-section";
        label.innerText = name;
        keyAreaComponent.setAttribute("readonly", true);
        keyAreaComponent.setAttribute("rows", "6");

        section.appendChild(label);
        section.appendChild(keyAreaComponent.renderer.rootElement);
        section.appendChild(copyButton.renderer.rootElement);

        return section;
    }

    _createActionsContainer() {
        const container = document.createElement("section");
        container.className = "actions-container";
        container.appendChild(this._closeButton.renderer.rootElement);
        return container;
    }
    /********************************************/
    /* END PRIVATE RENDERER METHODS             */
    /* START HANDLER METHODS                    */
    /********************************************/
    /**
     *
     * @param {{privateKey: string, publicKey: string}} data
     * @private
     */
    _onData({ privateKey = "", publicKey = "" } = {}) {
        this._privateKeyArea.setValue(privateKey);
        this._publicKeyArea.setValue(publicKey);
    }

    /********************************************/
    /* END HANDLER METHODS                      */
    /* START PRIVATE API METHODS                */
    /********************************************/

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /* START PUBLIC API METHODS                */
    /********************************************/

    /*******************************************/
    /*  END PUBLIC API METHODS                */
    /*******************************************/
}

module.exports = ShowKeysRenderer;
